import { getStationChannelPrefix } from "../station-addressing";
import { ENVELOPE_LAYOUT_VERSION, LEVEL_SPACINGS, POINTS_PER_TILE } from "./envelope-config";
/** Duration of one tile in seconds at the given level. */
export function getTileDuration(level) {
    return LEVEL_SPACINGS[level] * POINTS_PER_TILE[level];
}
/**
 * Returns the tile index that contains the given timestamp (seconds since Unix epoch).
 */
export function getTileIndex(timestamp, level) {
    return Math.floor(timestamp / getTileDuration(level));
}
/**
 * Returns the time range [start, end) in seconds for a given tile.
 */
export function getTileTimeRange(level, tileIndex) {
    const duration = getTileDuration(level);
    return {
        start: tileIndex * duration,
        end: (tileIndex + 1) * duration,
    };
}
/**
 * Returns all tile indices that overlap the viewport [startTime, endTime).
 * Returns an empty array if startTime >= endTime.
 */
export function getTileIndicesForViewport(startTime, endTime, level) {
    if (startTime >= endTime)
        return [];
    const firstTile = getTileIndex(startTime, level);
    // endTime is exclusive — subtract a tiny epsilon so we don't include the next tile
    // if endTime falls exactly on a tile boundary.
    const lastTile = getTileIndex(endTime - 1e-9, level);
    const indices = [];
    for (let i = firstTile; i <= lastTile; i++) {
        indices.push(i);
    }
    return indices;
}
/**
 * Returns the point index within a tile for a given timestamp.
 */
export function getPointIndexInTile(timestamp, level, tileIndex) {
    const tileRange = getTileTimeRange(level, tileIndex);
    return Math.floor((timestamp - tileRange.start) / LEVEL_SPACINGS[level]);
}
/**
 * Constructs the S3 object key for a tile.
 * Format: {network}_{station}/{location}/{channel}/L{level}/{tileIndex}
 */
export function getTileS3Key(stationData, level, tileIndex) {
    return `${getStationChannelPrefix(stationData)}/L${level}/${tileIndex}`;
}
/**
 * Constructs the S3 root for tile keys, which includes the current layout version.
 */
export function getS3Root(prefix) {
    return `${prefix}v${ENVELOPE_LAYOUT_VERSION}/`;
}
//# sourceMappingURL=data:application/json;base64,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